import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Html5Qrcode } from "html5-qrcode";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Camera, X, CheckCircle2, Trash2 } from "lucide-react";

/**
 * BarcodeScanner (multi-detection).
 *
 * Continuous camera scan. Ogni barcode/QR rilevato viene aggiunto alla lista
 * (unico). Il tecnico tocca il codice che vuole per selezionarlo.
 *
 * Bug fix (schermo bianco iOS): fermiamo lo stream della fotocamera PRIMA
 * di chiudere il dialog e attendiamo l'esito con try/catch.
 *
 * Props:
 * - onDetected(code): callback quando l'utente sceglie un codice
 * - autoPickFirst (default false): se true, il primo codice rilevato viene
 *   selezionato automaticamente (comportamento vecchio single-shot)
 * - hideTrigger, open, onOpenChange: controllo esterno del dialog
 */
export default function BarcodeScanner({
  triggerLabel = "Scansiona con fotocamera",
  onDetected,
  autoPickFirst = false,
  testid = "open-scanner",
  open: openProp,
  onOpenChange,
  hideTrigger = false,
  continuous = false, // se true, dopo aver scelto un codice lo scanner resta aperto
}) {
  const { t } = useTranslation();
  const isControlled = typeof openProp === "boolean";
  const [openInternal, setOpenInternal] = useState(false);
  const open = isControlled ? openProp : openInternal;
  const setOpen = (v) => {
    if (isControlled) onOpenChange?.(v);
    else setOpenInternal(v);
  };

  const [error, setError] = useState("");
  const [starting, setStarting] = useState(false);
  const [detected, setDetected] = useState([]);
  const [lastPicked, setLastPicked] = useState(""); // per feedback in modalità continua
  const detectedSetRef = useRef(new Set());
  const scannerRef = useRef(null);
  const onDetectedRef = useRef(onDetected);
  const autoPickFirstRef = useRef(autoPickFirst);
  const continuousRef = useRef(continuous);
  const pickedRef = useRef(false);
  const elementId = "qr-scanner-region";

  useEffect(() => { onDetectedRef.current = onDetected; }, [onDetected]);
  useEffect(() => { autoPickFirstRef.current = autoPickFirst; }, [autoPickFirst]);
  useEffect(() => { continuousRef.current = continuous; }, [continuous]);

  const stopScanner = useCallback(async () => {
    const s = scannerRef.current;
    scannerRef.current = null;
    if (!s) return;
    try {
      if (typeof s.isScanning !== "undefined" ? s.isScanning : true) {
        await s.stop();
      }
    } catch (e) { /* ignore */ }
    try { await s.clear(); } catch (e) { /* ignore */ }
  }, []);

  const pickCode = useCallback(async (text) => {
    if (pickedRef.current) return;
    pickedRef.current = true;
    if (navigator.vibrate) { try { navigator.vibrate(80); } catch (e) {} }
    const cb = onDetectedRef.current;
    if (continuousRef.current) {
      // Modalità continua: NON chiude lo scanner, resetta e rimuove il codice
      // dalla lista + azzera pickedRef così può accettarne un altro.
      setLastPicked(text);
      detectedSetRef.current.delete(text);
      setDetected((prev) => prev.filter((c) => c !== text));
      pickedRef.current = false;
      if (typeof cb === "function") cb(text);
      return;
    }
    await stopScanner();
    setOpen(false);
    if (typeof cb === "function") cb(text);
  }, [stopScanner]);

  useEffect(() => {
    if (!open) {
      detectedSetRef.current = new Set();
      setDetected([]);
      setLastPicked("");
      pickedRef.current = false;
      return;
    }
    let cancelled = false;
    setError("");
    setStarting(true);
    pickedRef.current = false;

    const onScanSuccess = (text) => {
      if (!text) return;
      if (detectedSetRef.current.has(text)) return;
      detectedSetRef.current.add(text);
      setDetected((prev) => [...prev, text]);
      if (navigator.vibrate) { try { navigator.vibrate(30); } catch (e) {} }
      if (autoPickFirstRef.current && !pickedRef.current) {
        pickCode(text);
      }
    };

    const start = async () => {
      try {
        await new Promise((r) => setTimeout(r, 100));
        if (cancelled) return;
        const html5 = new Html5Qrcode(elementId, { verbose: false });
        scannerRef.current = html5;
        const config = { fps: 10, qrbox: { width: 280, height: 180 }, aspectRatio: 1.333 };
        try {
          await html5.start({ facingMode: "environment" }, config, onScanSuccess, () => {});
          setStarting(false);
        } catch (e1) {
          // Fallback: nessun facingMode
          const msg = String(e1?.message || e1 || "");
          if (msg.toLowerCase().includes("notfound") || msg.toLowerCase().includes("overconstrained") || msg.toLowerCase().includes("permission") || msg.toLowerCase().includes("notallowed")) {
            try {
              await html5.start(undefined, { fps: 10, qrbox: { width: 280, height: 180 } }, onScanSuccess, () => {});
              setStarting(false);
              return;
            } catch (e2) {
              setError(String(e2?.message || e2));
              setStarting(false);
              return;
            }
          }
          setError(msg);
          setStarting(false);
        }
      } catch (e) {
        setStarting(false);
        setError(String(e?.message || e));
      }
    };
    start();

    return () => {
      cancelled = true;
      stopScanner();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const clearDetected = () => {
    detectedSetRef.current = new Set();
    setDetected([]);
  };

  const handleDialogChange = async (v) => {
    if (!v) {
      // chiusura utente → stoppa prima di smontare
      await stopScanner();
    }
    setOpen(v);
  };

  return (
    <>
      {!hideTrigger && (
        <Button type="button" variant="outline" className="rounded-sm" onClick={() => setOpen(true)} data-testid={testid}>
          <Camera className="mr-2 h-4 w-4" /> {triggerLabel}
        </Button>
      )}
      <Dialog open={open} onOpenChange={handleDialogChange}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              Inquadra i barcode
              {continuous && (
                <span className="rounded-sm bg-emerald-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-emerald-800">
                  🔁 Continuo
                </span>
              )}
            </DialogTitle>
          </DialogHeader>

          {continuous && lastPicked && (
            <div className="rounded-sm border border-emerald-200 bg-emerald-50 p-2 text-xs text-emerald-800" data-testid="scanner-last-picked">
              ✅ Ultimo aggiunto: <span className="font-mono font-bold">{lastPicked}</span> — spara il prossimo o premi <b>Chiudi</b> quando hai finito
            </div>
          )}

          <div id={elementId} className="overflow-hidden rounded-sm bg-slate-900" style={{ minHeight: 280 }} />

          {starting && !error && (
            <div className="rounded-sm border border-slate-200 bg-slate-50 p-2 text-center text-xs text-slate-600">
              Avvio fotocamera…
            </div>
          )}
          {error && (
            <div className="rounded-sm border border-red-200 bg-red-50 p-3 text-xs text-red-700">
              {error}<br/><b>Suggerimenti:</b>
              <ul className="ml-4 mt-1 list-disc">
                <li>{t("auto.consenti_laccesso_alla_fotocamera_dalle_impostazioni_del_bro")}</li>
                <li>{t("auto.chiudi_altre_app_che_usano_la_fotocamera")}</li>
                <li>{t("auto.prova_a_ricaricare_la_pagina")}</li>
              </ul>
            </div>
          )}

          {/* Lista codici rilevati */}
          <div className="rounded-sm border border-slate-200 bg-white p-2" data-testid="scanner-results">
            <div className="mb-1 flex items-center justify-between">
              <div className="text-[11px] font-bold uppercase tracking-widest text-slate-500">
                Codici rilevati <span className="ml-1 rounded-sm bg-slate-100 px-1.5 py-0.5 font-mono text-slate-700">{detected.length}</span>
              </div>
              {detected.length > 0 && (
                <button type="button" onClick={clearDetected} className="text-[11px] text-slate-500 hover:text-red-600" data-testid="scanner-clear">
                  <Trash2 className="mr-1 inline h-3 w-3" /> Pulisci
                </button>
              )}
            </div>
            {detected.length === 0 ? (
              <div className="py-3 text-center text-[11px] italic text-slate-400">
                Inquadra la scatola: rileveremo tutti i barcode. Tocca quello giusto qui sotto.
              </div>
            ) : (
              <div className="max-h-48 space-y-1 overflow-y-auto">
                {detected.map((code, i) => (
                  <button
                    key={code}
                    type="button"
                    onClick={() => pickCode(code)}
                    className="flex w-full items-center justify-between rounded-sm border border-slate-200 bg-white px-2 py-2 text-left transition hover:border-[#0052FF] hover:bg-blue-50 active:bg-blue-100"
                    data-testid={`scanner-pick-${i}`}
                  >
                    <div className="flex items-center gap-2">
                      <span className="rounded-sm bg-slate-100 px-1.5 py-0.5 font-mono text-[10px] text-slate-500">#{i + 1}</span>
                      <span className="font-mono text-sm font-semibold text-slate-900">{code}</span>
                    </div>
                    <span className="inline-flex items-center gap-1 rounded-sm bg-[#0052FF] px-2 py-1 text-[10px] font-bold uppercase text-white">
                      <CheckCircle2 className="h-3 w-3" /> Scegli
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>

          <Button variant="outline" onClick={() => handleDialogChange(false)} className="rounded-sm" data-testid="close-scanner">
            <X className="mr-2 h-4 w-4" /> {t("auto.chiudi")}
          </Button>
        </DialogContent>
      </Dialog>
    </>
  );
}
